/**
 * GdsFlightCard — compact, boarding-pass style flight rendering parsed from the
 * free-text GDS flight blob (`itinerary.flightDetails`) when no structured
 * FlightVoucher exists for the trip.
 *
 * One block per flight DIRECTION (outbound / return). Connected segments (same
 * handover airport + layover < 24h) are grouped into a single direction and
 * BOTH legs are shown with the layover/stopover duration between them. A >24h
 * gap (destination stay) or a route break starts a new direction.
 *
 * Per leg we show: airline logo, airline name · flight no · aircraft (when
 * available) · leg duration, and the ORIG hh:mm ✈ DEST hh:mm (+ND) route line.
 *
 * Compact + borderless (no hard boxes). Reuses the voucher card's <AirlineLogo>
 * (multi-CDN chain + PDF-safe crossOrigin). Tabler `ti-plane` isn't loaded so we
 * use lucide <Plane> rotated 45°. Uses the requested --color-* tokens with safe
 * fallbacks (those tokens aren't defined in this codebase yet).
 */
import React from 'react';
import { Plane } from 'lucide-react';
import { AirlineLogo } from '@/components/itinerary/ItineraryFlightCard';

const AIRLINE_COLORS = {
  '6E': '#00308F', AI: '#C8102E', UK: '#E03A3E', SG: '#FF6C00', QR: '#5C0632', EK: '#D71921',
};
const airlineColor = (code) => AIRLINE_COLORS[(code || '').toUpperCase()] || '#374151';

const AIRLINE_NAMES = {
  '6E': 'IndiGo', AI: 'Air India', UK: 'Vistara', SG: 'SpiceJet', QR: 'Qatar Airways',
  EK: 'Emirates', MK: 'Air Mauritius', SQ: 'Singapore Airlines', TG: 'Thai Airways',
  CX: 'Cathay Pacific', LH: 'Lufthansa', EY: 'Etihad', WY: 'Oman Air',
  BA: 'British Airways', AA: 'American Airlines',
};
export const airlineName = (code) => AIRLINE_NAMES[(code || '').toUpperCase()] || (code || '').toUpperCase();
// Strict variant for the WhatsApp text quote — no code-as-fallback, blank
// when the airline isn't in the known-names map (never guess a name).
export const knownAirlineName = (code) => AIRLINE_NAMES[(code || '').toUpperCase()] || '';

const AIRPORT_NAMES = {
  DEL: 'New Delhi', BOM: 'Mumbai', BLR: 'Bengaluru', MAA: 'Chennai', CCU: 'Kolkata',
  HYD: 'Hyderabad', COK: 'Kochi', GOI: 'Goa', GOX: 'Goa (Mopa)', PNQ: 'Pune',
  AMD: 'Ahmedabad', JAI: 'Jaipur', IXL: 'Leh', SXR: 'Srinagar', IXC: 'Chandigarh',
  LKO: 'Lucknow', PAT: 'Patna', GAU: 'Guwahati', TRV: 'Thiruvananthapuram',
  VTZ: 'Visakhapatnam', NAG: 'Nagpur', BBI: 'Bhubaneswar', IXB: 'Bagdogra',
  MRU: 'Mauritius', DXB: 'Dubai', AUH: 'Abu Dhabi', DOH: 'Doha', SIN: 'Singapore',
  BKK: 'Bangkok', DMK: 'Bangkok (Don Mueang)', HKT: 'Phuket', KUL: 'Kuala Lumpur',
  CMB: 'Colombo', MLE: 'Malé', KTM: 'Kathmandu', DPS: 'Bali (Denpasar)',
  HKG: 'Hong Kong', LHR: 'London', JFK: 'New York', CDG: 'Paris', IST: 'Istanbul',
  NRT: 'Tokyo', ICN: 'Seoul', SYD: 'Sydney',
};
export const airportName = (code) => AIRPORT_NAMES[(code || '').toUpperCase()] || '';

// IATA → IANA time zone, for real (DST-aware) leg durations. Deliberately
// only the codes we actually recognise above — an airport missing here means
// duration must be OMITTED, never guessed (see durationUnknown below).
const AIRPORT_TIMEZONES = {
  DEL: 'Asia/Kolkata', BOM: 'Asia/Kolkata', BLR: 'Asia/Kolkata', MAA: 'Asia/Kolkata',
  CCU: 'Asia/Kolkata', HYD: 'Asia/Kolkata', COK: 'Asia/Kolkata', GOI: 'Asia/Kolkata',
  GOX: 'Asia/Kolkata', PNQ: 'Asia/Kolkata', AMD: 'Asia/Kolkata', JAI: 'Asia/Kolkata',
  IXL: 'Asia/Kolkata', SXR: 'Asia/Kolkata', IXC: 'Asia/Kolkata', LKO: 'Asia/Kolkata',
  PAT: 'Asia/Kolkata', GAU: 'Asia/Kolkata', TRV: 'Asia/Kolkata', VTZ: 'Asia/Kolkata',
  NAG: 'Asia/Kolkata', BBI: 'Asia/Kolkata', IXB: 'Asia/Kolkata',
  MRU: 'Indian/Mauritius', DXB: 'Asia/Dubai', AUH: 'Asia/Dubai', DOH: 'Asia/Qatar',
  SIN: 'Asia/Singapore', BKK: 'Asia/Bangkok', DMK: 'Asia/Bangkok', HKT: 'Asia/Bangkok',
  KUL: 'Asia/Kuala_Lumpur', CMB: 'Asia/Colombo', MLE: 'Indian/Maldives', KTM: 'Asia/Kathmandu',
  DPS: 'Asia/Makassar', HKG: 'Asia/Hong_Kong', LHR: 'Europe/London', JFK: 'America/New_York',
  CDG: 'Europe/Paris', IST: 'Europe/Istanbul', NRT: 'Asia/Tokyo', ICN: 'Asia/Seoul', SYD: 'Australia/Sydney',
};
const airportTimeZone = (code) => AIRPORT_TIMEZONES[(code || '').toUpperCase()] || '';

const MONTHS = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };

function parseGdsDate(s) {
  const m = /^(\d{1,2})([A-Za-z]{3})(\d{2,4})$/.exec((s || '').trim());
  if (!m) return null;
  const mon = MONTHS[m[2].toUpperCase()];
  if (mon == null) return null;
  let yr = parseInt(m[3], 10);
  if (yr < 100) yr += 2000;
  return new Date(yr, mon, parseInt(m[1], 10));
}

// Normalise any common GDS time token → { time: 'HH:MM', dayOffset }.
// Handles 05:30, 5:30, 18:45, 0530, 1130, 0530h, with optional day-offset
// suffix written as (+1) or +1. Returns null when it isn't a valid time.
function normalizeGdsTime(tk) {
  let s = String(tk == null ? '' : tk).trim();
  if (!s) return null;
  let dayOffset = 0;
  const off = /\(\+?(\d+)\)$|\+(\d+)$/.exec(s);
  if (off) {
    dayOffset = parseInt(off[1] || off[2], 10) || 0;
    s = s.slice(0, off.index);
  }
  s = s.replace(/h$/i, '').trim();
  let hh; let mm;
  const colon = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (colon) { hh = parseInt(colon[1], 10); mm = parseInt(colon[2], 10); }
  else {
    const four = /^(\d{2})(\d{2})$/.exec(s); // 0530 / 1130
    if (four) { hh = parseInt(four[1], 10); mm = parseInt(four[2], 10); }
    else return null;
  }
  if (Number.isNaN(hh) || Number.isNaN(mm) || hh > 23 || mm > 59) return null;
  return { time: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`, dayOffset };
}

// Real UTC-offset minutes a given time zone reads at a given UTC instant —
// DST-aware because it asks the actual date, not a hardcoded offset table.
function tzOffsetMinutes(utcGuess, timeZone) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(utcGuess).forEach((p) => { parts[p.type] = p.value; });
  const hour = parts.hour === '24' ? 0 : parseInt(parts.hour, 10);
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, hour, +parts.minute, +parts.second);
  return Math.round((asUTC - utcGuess.getTime()) / 60000);
}

// A GDS date+time+day-offset, read as LOCAL wall-clock time in `timeZone` →
// the real UTC epoch ms. Returns null if the airport's time zone is unknown
// (caller must omit the duration, never guess a duration from raw clock diff).
function toInstant(dateStr, timeStr, dayOffset = 0, timeZone = '') {
  if (!timeZone) return null;
  const d = parseGdsDate(dateStr);
  const t = normalizeGdsTime(timeStr);
  if (!d || !t) return null;
  d.setDate(d.getDate() + (dayOffset || 0));
  const guess = new Date(Date.UTC(
    d.getFullYear(), d.getMonth(), d.getDate(),
    parseInt(t.time.slice(0, 2), 10), parseInt(t.time.slice(3), 10),
  ));
  const offsetMin = tzOffsetMinutes(guess, timeZone);
  return guess.getTime() - offsetMin * 60000;
}

// One GDS line → a single leg.
function parseLine(line) {
  const tokens = String(line).trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;
  let date = '';
  const times = [];
  const airports = [];
  const terminals = [];
  const flights = [];

  for (let i = 0; i < tokens.length; i++) {
    const tk = tokens[i];
    if (tk === '-' || tk === '/') continue;
    const tt = normalizeGdsTime(tk);
    if (tt) { times.push(tt); continue; }
    if (!date && /^\d{1,2}[A-Za-z]{3}\d{2,4}$/.test(tk)) { date = tk; continue; }
    if (/^[A-Z]{3}$/.test(tk)) {
      airports.push(tk);
      // Optional "T2"/"T3" terminal token right after an airport code —
      // only recorded when actually present, never guessed.
      const nextTk = tokens[i + 1];
      if (nextTk && /^T\d{1,2}$/i.test(nextTk)) { terminals.push(nextTk.toUpperCase()); i += 1; }
      else terminals.push('');
      continue;
    }
    if (/^[0-9A-Z]{2}$/.test(tk)) {
      let j = i + 1;
      while (j < tokens.length && (tokens[j] === '-' || tokens[j] === '/')) j += 1;
      if (j < tokens.length && /^\d{1,4}[A-Z]?$/.test(tokens[j])) {
        flights.push({ code: tk.toUpperCase(), number: tokens[j] });
        i = j;
      }
      continue;
    }
  }
  if (airports.length < 2) return null;
  const dep = times[0] || {};
  const arr = times[times.length - 1] || {};
  let arrOffset = arr.dayOffset || 0;
  if (!arrOffset && dep.time && arr.time && arr.time < dep.time) arrOffset = 1;
  return {
    date,
    origin: airports[0],
    dest: airports[airports.length - 1],
    originTerminal: terminals[0] || '',
    destTerminal: terminals[terminals.length - 1] || '',
    depTime: dep.time || '',
    arrTime: arr.time || '',
    arrDayOffset: arrOffset,
    flights,
    aircraft: '',
  };
}

function layoverMins(prev, next) {
  const a = toInstant(prev.date, prev.arrTime, prev.arrDayOffset, airportTimeZone(prev.dest));
  const b = toInstant(next.date, next.depTime, 0, airportTimeZone(next.origin));
  if (a == null || b == null) return null;
  return Math.round((b - a) / 60000);
}

function fmtDur(mins) {
  if (mins == null || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(' ') || '0m';
}

function fmtGdsDate(dateStr) {
  const d = parseGdsDate(dateStr);
  return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (dateStr || '');
}

// Public: parse the whole blob → array of directions, each carrying every leg.
export function parseGdsFlights(text) {
  if (!text || !String(text).trim()) return [];
  const hops = String(text).split(/\r?\n/).map(parseLine).filter(Boolean);
  if (hops.length === 0) return [];

  const grouped = [];
  let current = [];
  for (let i = 0; i < hops.length; i++) {
    const h = hops[i];
    if (current.length === 0) { current = [h]; continue; }
    const prev = current[current.length - 1];
    const mins = layoverMins(prev, h);
    const connects = prev.dest === h.origin && mins != null && mins > 0 && mins < 1440;
    if (connects) current.push(h);
    else { grouped.push(current); current = [h]; }
  }
  if (current.length) grouped.push(current);

  return grouped.map((hopsArr) => {
    const legs = hopsArr.map((h) => {
      const depTz = airportTimeZone(h.origin);
      const arrTz = airportTimeZone(h.dest);
      const missingTzCode = !depTz ? h.origin : (!arrTz ? h.dest : '');
      let durationMins = null;
      if (!missingTzCode) {
        const depI = toInstant(h.date, h.depTime, 0, depTz);
        const arrI = toInstant(h.date, h.arrTime, h.arrDayOffset, arrTz);
        durationMins = (depI != null && arrI != null) ? Math.round((arrI - depI) / 60000) : null;
      }
      return {
        date: h.date,
        origin: h.origin,
        dest: h.dest,
        originTerminal: h.originTerminal || '',
        destTerminal: h.destTerminal || '',
        depTime: h.depTime,
        arrTime: h.arrTime,
        arrDayOffset: h.arrDayOffset,
        airline: (h.flights[0] || {}).code || '',
        flightLabel: h.flights.map((f) => `${f.code} ${f.number}`).join(' / '),
        aircraft: h.aircraft || '',
        durationMins,
        durationUnknown: !!missingTzCode || durationMins == null,
        missingTzCode,
      };
    });
    const layovers = [];
    for (let i = 0; i < hopsArr.length - 1; i++) {
      layovers.push({ code: hopsArr[i].dest, mins: layoverMins(hopsArr[i], hopsArr[i + 1]) });
    }
    return { legs, layovers, depDate: hopsArr[0].date };
  });
}

const TK = {
  borderSecondary: 'var(--color-border-secondary, #e2e8f0)',
  textTertiary: 'var(--color-text-tertiary, #94a3b8)',
};

const LegRow = ({ leg, idx }) => {
  const sub = [airlineName(leg.airline), leg.flightLabel, leg.aircraft, fmtDur(leg.durationMins)].filter(Boolean).join('   ·   ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0' }} data-testid={`gds-leg-${idx}`}>
      <AirlineLogo code={leg.airline} name={airlineName(leg.airline)} primary={airlineColor(leg.airline)} testId={`gds-leg-logo-${idx}`} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', flexWrap: 'wrap', color: '#0f172a' }}>
          <span style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '-0.01em' }}>{leg.origin}</span>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>{leg.depTime || '—'}</span>
          <Plane size={13} style={{ transform: 'rotate(45deg)', color: '#94a3b8' }} />
          <span style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '-0.01em' }}>{leg.dest}</span>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
            {leg.arrTime || '—'}
            {leg.arrDayOffset > 0 && <sup style={{ color: '#D97706', fontSize: '9px', fontWeight: 700, marginLeft: '2px' }}>+{leg.arrDayOffset}D</sup>}
          </span>
        </div>
        {sub && <div style={{ fontSize: '11px', color: TK.textTertiary, marginTop: '2px' }}>{sub}</div>}
      </div>
    </div>
  );
};

const LayoverRow = ({ lay }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 0 1px 54px', fontSize: '10px', color: TK.textTertiary }}>
    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#D97706', display: 'inline-block' }} />
    {fmtDur(lay.mins)} layover in {lay.code}
  </div>
);

export function GdsFlightCard({ direction, index, total }) {
  const label = total <= 2 ? (index === 0 ? 'Outbound' : 'Return') : `Flight ${index + 1}`;
  // Flatten legs + valid layovers into a single ordered row list (keeps the
  // JSX map shallow — no nested member lookups).
  const rows = [];
  direction.legs.forEach((leg, i) => {
    rows.push({ kind: 'leg', leg, key: `leg-${i}`, idx: i });
    const lay = direction.layovers[i];
    if (lay && lay.mins != null && lay.mins > 0 && lay.mins < 1440) {
      rows.push({ kind: 'lay', lay, key: `lay-${i}` });
    }
  });
  return (
    <div data-testid={`gds-flight-card-${index}`} className="pdf-no-break">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a' }}>{label}</span>
        {direction.depDate && <span style={{ fontSize: '11px', color: TK.textTertiary }}>{fmtGdsDate(direction.depDate)}</span>}
      </div>
      {rows.map((r) => (r.kind === 'leg'
        ? <LegRow key={r.key} leg={r.leg} idx={r.idx} />
        : <LayoverRow key={r.key} lay={r.lay} />))}
    </div>
  );
}

export function GdsFlightList({ directions }) {
  if (!directions || directions.length === 0) return null;
  // Two directions → side-by-side with a vertical partition (Outbound | Return).
  // One or 3+ → stacked, separated by a hairline.
  const twoUp = directions.length === 2;
  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="itinerary-flight-gds">
      {twoUp ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', alignItems: 'stretch' }}>
          <div style={{ padding: '18px 20px' }}><GdsFlightCard direction={directions[0]} index={0} total={2} /></div>
          <div style={{ background: TK.borderSecondary }} aria-hidden="true" />
          <div style={{ padding: '18px 20px' }}><GdsFlightCard direction={directions[1]} index={1} total={2} /></div>
        </div>
      ) : (
        <div>
          {directions.map((dir, i) => (
            <div key={i} style={{ padding: '18px 20px', borderTop: i > 0 ? `1px solid ${TK.borderSecondary}` : 'none' }}>
              <GdsFlightCard direction={dir} index={i} total={directions.length} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GdsFlightCard;
